import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BrandService } from '../brand/brand.service';
import { ParentLoginService } from './parent-login.service';
import { ParentInsight, ParentInsightsService } from './parent-insights.service';

declare var $: any;

// Parent Login — served at parent.<domainSuffix> (see AppComponent's
// subdomain switch, same pattern as app./registration.). Wired to the
// parent mobile app's own OTP endpoints (generate/verify, mobile or email)
// so a web login produces the same session shape the app already expects —
// see the localStorage keys set in onLoginSuccess(), matching the app's
// AsyncStorage keys (studentDetails/authToken/mobileNumber/userLoginedMode)
// so anything reading those later stays interchangeable with the app.
@Component({
  selector: 'app-parent-login',
  templateUrl: './parent-login.component.html',
  styleUrls: ['./parent-login.component.css'],
})
export class ParentLoginComponent implements AfterViewInit, OnDestroy {

  brand = this.brandService.brand;

  // Login methods come from the brand configuration (parentMobileLogin /
  // parentEmailLogin). Both off is treated as mobile-only so a bad config can
  // never leave the portal without a way to sign in.
  get mobileLoginEnabled(): boolean {
    const b = this.brandService.brand;
    return b.parentMobileLogin !== false || b.parentEmailLogin === false;
  }

  get emailLoginEnabled(): boolean {
    return this.brandService.brand.parentEmailLogin !== false;
  }

  get showLoginModeSwitch(): boolean {
    return this.mobileLoginEnabled && this.emailLoginEnabled;
  }

  loginMode: 'mobile' | 'email' = this.mobileLoginEnabled ? 'mobile' : 'email';

  mobileNumber = '';
  countryDialCode = this.brandService.brand.defaultCountryDialCode || '91';
  emailId = '';

  otpSent = false;
  otpRequestInFlight = false;
  errorMessage = '';
  // Dashboard-level error surface — separate from errorMessage (which is
  // the login FORM's own error slot, rendered nowhere on the dashboard) so
  // a failed switchStudent() while already logged in is actually visible
  // instead of silently doing nothing.
  switchStudentError = '';
  // Bumped on every successful switchStudent() call. ParentLeaveRequestComponent
  // takes no @Input() at all (the backend resolves student/class from the
  // auth token, not request params — see its own service comment), so it
  // has nothing for ngOnChanges to react to when the active student
  // changes; it watches this counter instead to know when to reload.
  studentSwitchTick = 0;

  // Set by My Teacher's Message button: Chat opens straight into this staff member's conversation.
  chatTargetStaffId: string | null = null;

  get currentClassLabel(): string {
    return this.studentClassLabel(this.storedStudent);
  }

  openChatWithTeacher(staffId: string): void {
    this.chatTargetStaffId = staffId;
    this.showSection('chat');
  }

  otp: string[] = ['', '', '', '', '', ''];
  otpErrorMessage = '';
  otpVerifyInFlight = false;
  resendTimeLeft = 0;
  private resendInterval: any;

  // Persists across a page refresh/reopen — a stored authToken means an
  // earlier OTP verify already succeeded, so re-show the dashboard instead
  // of bouncing back to the login form (matching the mobile app's own
  // "already logged in" behavior via AsyncStorage).
  loginSuccessful = !!localStorage.getItem('authToken');

  insights: ParentInsight[] = [];
  insightsLoading = false;
  insightsEnabled = false;
  selectedInsight: ParentInsight | null = null;

  // Which sidebar section is showing in .pd-main — only 'dashboard' and
  // 'diary' are wired to a real screen so far; the rest of the sidebar
  // items (attendance, timetable, fees, chat, broadcast, ...) are still
  // static per the sidebar-shell scope from earlier. Initialized from the
  // current URL (/parent/diary vs /parent/dashboard) since this component
  // is routed under both paths — see app-routing.module.ts.
  activeSection: 'dashboard' | 'diary' | 'attendance' | 'timetable' | 'leaveRequest' | 'fees' | 'documentHub' | 'broadcast' | 'chat' | 'notifications' | 'myTeacher' | 'activity' | 'feedback' | 'academicCalendar' =
    ParentLoginComponent.resolveInitialSection();

  // Hash-based initial section, same lookup chain as before, but run through
  // a static helper (rather than inline in the field initializer) so a
  // gating check can be layered on below — a stale deep link (e.g. a
  // bookmarked /parent/chat hash) landing on a section now disabled for
  // whichever student is currently active would otherwise render a blank
  // .pd-main with no matching *ngIf, since every gated tile's content block
  // is wrapped the same way its nav item is.
  private static resolveInitialSection(): 'dashboard' | 'diary' | 'attendance' | 'timetable' | 'leaveRequest' | 'fees' | 'documentHub' | 'broadcast' | 'chat' | 'notifications' | 'myTeacher' | 'activity' | 'feedback' | 'academicCalendar' {
    const hash = window.location.hash;
    const section = hash.includes('/parent/diary') ? 'diary'
      : hash.includes('/parent/attendance') ? 'attendance'
      : hash.includes('/parent/timetable') ? 'timetable'
      : hash.includes('/parent/leave-request') ? 'leaveRequest'
      : hash.includes('/parent/fees') ? 'fees'
      : hash.includes('/parent/document-hub') ? 'documentHub'
      : hash.includes('/parent/broadcast') ? 'broadcast'
      : hash.includes('/parent/chat') ? 'chat'
      : hash.includes('/parent/notifications') ? 'notifications'
      : hash.includes('/parent/my-teacher') ? 'myTeacher'
      : hash.includes('/parent/activity') ? 'activity'
      : hash.includes('/parent/feedback') ? 'feedback'
      : hash.includes('/parent/academic-calendar') ? 'academicCalendar' : 'dashboard';
    return section;
  }

  // Multi-child switching (see switchStudent()) — mirrors the mobile app's
  // 3-avatar-strip + "+N" overflow pattern. showAllStudentsModal is the
  // "+N" overflow list; tapping the active avatar opens showPhotoPreview
  // instead of switching (it's already the active student).
  //
  // Persistence across reloads: unlike the mobile app (which re-fetches
  // the sibling list from getAllKids() on every load and re-splices the
  // SELECTED_STUDENT id to the front of that fresh list), this web login
  // only ever has the localStorage-persisted studentDetails array as its
  // source of truth — there's no separate "kids" API in scope here — and
  // switchStudent() already writes that array back in splice-to-front
  // order on every switch. So on reload it's already exactly as it was
  // left; no separate reconciliation step is needed.
  showAllStudentsModal = false;
  showPhotoPreview = false;

  // Mobile-only nav drawer — .pd-sidebar is display:none below 900px (see
  // .pd-shell's CSS) with no other way to reach it, so the topbar's
  // hamburger button toggles this to slide it in as an overlay drawer.
  // Unrelated to the desktop layout, where the sidebar is always visible
  // and this flag has no effect (its CSS is scoped inside the same
  // @media (max-width: 900px) block that hides the sidebar in the first place).
  mobileSidebarOpen = false;

  constructor(
    private brandService: BrandService,
    private parentLoginService: ParentLoginService,
    private parentInsightsService: ParentInsightsService,
    private router: Router,
    private translate: TranslateService,
  ) {
    if (this.loginSuccessful) {
      // A stale deep link (e.g. a bookmarked hash) may resolve to a section
      // that's gated off for whichever student is currently active — fall
      // back to 'dashboard' (always enabled) rather than leave activeSection
      // pointed at a tile whose *ngIf now hides both its nav item and its
      // main-content block, which would otherwise render a blank .pd-main.
      if (!this.isSectionEnabled(this.activeSection)) {
        this.activeSection = 'dashboard';
      }
      this.fetchInsights();
      this.fetchKidDetails();
    }
  }

  // Same intlTelInput setup used on the registration/admission mobile
  // fields (see new-school-registration.component.ts) — initializes to the
  // brand's default country but lets the parent pick any country/dial code.
  ngAfterViewInit(): void {
    this.initMobileNumberInput();
  }

  ngOnDestroy(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  private initMobileNumberInput(): void {
    if (!$.fn || !$.fn.intlTelInput) {
      return;
    }
    const mobileInput = $('#parent-mobile-number');
    if (!mobileInput.length || mobileInput.data('intlTelInput')) {
      return;
    }
    mobileInput.intlTelInput({
      initialCountry: this.brand.defaultCountryIso2Lower || 'in',
      separateDialCode: true,
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/11.0.4/js/utils.js',
    });
    mobileInput.data('intlTelInput', true);
    mobileInput.on('countrychange', () => {
      this.countryDialCode = mobileInput.intlTelInput('getSelectedCountryData').dialCode;
    });
  }

  setLoginMode(mode: 'mobile' | 'email'): void {
    if (this.loginMode === mode) {
      return;
    }
    this.loginMode = mode;
    this.errorMessage = '';
    // The mobile-number input only exists in the DOM while loginMode ===
    // 'mobile' (see *ngIf in the template), so intlTelInput must be
    // re-initialized each time it's switched back into view.
    if (mode === 'mobile') {
      setTimeout(() => this.initMobileNumberInput());
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  sendOtp(): void {
    this.errorMessage = '';

    if (this.loginMode === 'mobile') {
      if (!this.mobileNumber || this.mobileNumber.trim().length < 6) {
        this.errorMessage = 'Please enter a valid mobile number';
        return;
      }
      this.otpRequestInFlight = true;
      this.parentLoginService.sendOtpForMobile(this.countryDialCode, this.mobileNumber.trim()).subscribe({
        next: (response: any) => {
          this.otpRequestInFlight = false;
          if (response && response.status) {
            this.onOtpSent();
          } else {
            this.errorMessage = (response && response.body && response.body.message) || 'Unable to send OTP. Please try again.';
          }
        },
        error: (error) => {
          this.otpRequestInFlight = false;
          this.errorMessage = this.extractErrorMessage(error, 'Unable to send OTP. Please try again.');
        },
      });
    } else {
      if (!this.emailId || !this.isValidEmail(this.emailId.trim())) {
        this.errorMessage = 'Please enter a valid email address';
        return;
      }
      this.otpRequestInFlight = true;
      this.parentLoginService.sendOtpForEmail(this.emailId.trim()).subscribe({
        next: (response: any) => {
          this.otpRequestInFlight = false;
          if (response && response.status) {
            this.onOtpSent();
          } else {
            this.errorMessage = (response && response.body && response.body.message) || 'Unable to send OTP. Please try again.';
          }
        },
        error: (error) => {
          this.otpRequestInFlight = false;
          this.errorMessage = this.extractErrorMessage(error, 'Unable to send OTP. Please try again.');
        },
      });
    }
  }

  private onOtpSent(): void {
    this.otpSent = true;
    this.otp = ['', '', '', '', '', ''];
    this.otpErrorMessage = '';
    this.startResendTimer();
  }

  private focusOtpInput(index: number): void {
    const input = document.getElementById('parent-otp-input-' + index) as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
    }
  }

  // keydown (before the box's value changes) — blocks non-digit keys so they
  // never land at all (a keyup-based preventDefault, the previous approach,
  // fires too late to stop the character from being typed), and jumps back
  // to the previous box on Backspace only when THIS box is already empty
  // (checked against the DOM value directly, not this.otp[index] — reading
  // the model here raced with ngModel's own update and made backspace
  // sometimes skip a box or land in the wrong one).
  handleOtpKeydown(event: KeyboardEvent, index: number): void {
    const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (event.key === 'Backspace') {
      const currentValue = (event.target as HTMLInputElement).value;
      if (!currentValue && index > 0) {
        this.focusOtpInput(index - 1);
      }
      return;
    }
    if (allowedControlKeys.includes(event.key) || event.metaKey || event.ctrlKey) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // input (after the box's value has actually changed) — advances to the
  // next box only once this one really holds a digit, sidestepping any
  // keydown/ngModel ordering race. Typing over an already-filled box (the
  // digit is selected on focus, see focusOtpInput) replaces it cleanly
  // instead of the old approach's tendency to leave stale characters.
  handleOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    this.otp[index] = digit;
    input.value = digit;
    if (digit && index < this.otp.length - 1) {
      this.focusOtpInput(index + 1);
    }
  }

  // Pasting a full OTP (e.g. copied from an SMS/email) anywhere in the
  // boxes fills all of them at once instead of dumping the whole string
  // into a single 1-character box.
  handleOtpPaste(event: ClipboardEvent, index: number): void {
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').split('');
    if (!digits.length) {
      return;
    }
    event.preventDefault();
    for (let i = 0; i < this.otp.length - index && i < digits.length; i++) {
      this.otp[index + i] = digits[i];
    }
    const lastFilledIndex = Math.min(index + digits.length, this.otp.length) - 1;
    this.focusOtpInput(lastFilledIndex);
  }

  trackByIndex(index: number): number {
    return index;
  }

  get otpComplete(): boolean {
    return this.otp.every(digit => digit.trim() !== '');
  }

  verifyOtp(): void {
    this.otpErrorMessage = '';
    if (!this.otpComplete) {
      this.otpErrorMessage = 'Please enter the complete 6-digit code';
      return;
    }
    const otpValue = this.otp.join('');
    this.otpVerifyInFlight = true;

    const verify$ = this.loginMode === 'mobile'
      ? this.parentLoginService.verifyOtpForMobile(otpValue, this.countryDialCode, this.mobileNumber.trim())
      : this.parentLoginService.verifyOtpForEmail(otpValue, this.emailId.trim());

    verify$.subscribe({
      next: (response: any) => {
        this.otpVerifyInFlight = false;
        if (response && response.status && response.body && response.body.schoolAndStudentDetailData) {
          this.onLoginSuccess(response.body.schoolAndStudentDetailData);
        } else {
          this.otpErrorMessage = (response && response.body && response.body.message) || 'Incorrect OTP. Please try again.';
        }
      },
      error: (error) => {
        this.otpVerifyInFlight = false;
        this.otpErrorMessage = this.extractErrorMessage(error, 'Incorrect OTP. Please try again.');
      },
    });
  }

  private onLoginSuccess(schoolAndStudentDetailData: any[]): void {
    // Mirrors the mobile app's AsyncStorage keys (LoginScreen.js) so the
    // same session shape is available to anything reading it later.
    localStorage.setItem('studentDetails', JSON.stringify(schoolAndStudentDetailData));
    localStorage.setItem('authToken', JSON.stringify(schoolAndStudentDetailData[0]?.session));
    localStorage.setItem('mobileNumber', JSON.stringify(this.loginMode === 'mobile' ? this.mobileNumber.trim() : this.emailId.trim()));
    localStorage.setItem('userLoginedMode', this.loginMode);
    // Restore-on-reload key (see students/switchStudent()) — set to the
    // first/active student from login, same as a fresh app install.
    localStorage.setItem('SELECTED_STUDENT', this.studentId(schoolAndStudentDetailData[0]));
    this.loginSuccessful = true;
    this.router.navigate(['/parent/dashboard']);

    // Called unconditionally on dashboard load — the PARENT_AI_INSIGHT
    // permission gate (schoolAndStudentDetailData[0].schoolExtensionServicePermissions)
    // was dropped for now since its exact field name/shape from this API
    // hasn't been confirmed against a real response; re-add it once that's
    // verified, following isParentAiInsightEnabled() in
    // parent-insights.service.ts.
    this.fetchInsights();
    this.fetchKidDetails();
  }

  // getAllKidInfoForSecondaryParent takes its identifier in the `mobileNumber`
  // field. For a mobile login that's the phone number; for an email login the
  // email address is sent in that same field (the 'mobileNumber' localStorage
  // key already holds whichever the parent logged in with — see
  // onLoginSuccess()), so both modes use the same call.
  private fetchKidDetails(): void {
    let mobileNumber = '';
    try {
      mobileNumber = JSON.parse(localStorage.getItem('mobileNumber') || '""');
    } catch {
      return;
    }
    if (!mobileNumber) {
      return;
    }
    this.parentLoginService.getAllKidInfoForSecondaryParent(mobileNumber).subscribe({
      next: (response: any) => {
        // This endpoint has been observed returning its array JSON-encoded
        // as a string (i.e. the HTTP body is itself a JSON string literal
        // containing the array's text) rather than as a true top-level JSON
        // array/object — Angular's HttpClient parses that outer layer fine,
        // handing back a plain string here, so it needs an extra JSON.parse
        // before the normal array/body-shape checks below can see it.
        let parsedResponse = response;
        if (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            return;
          }
        }
        const kids = Array.isArray(parsedResponse) ? parsedResponse : (parsedResponse?.body || null);
        if (!Array.isArray(kids) || !kids.length) {
          return;
        }
        // Merge studentImage (and anything else this endpoint carries)
        // into the existing splice-to-front-ordered array by studentId,
        // rather than replacing it outright — switchStudent()'s ordering
        // and the active session must survive this merge untouched.
        const byId = new Map(kids.map((k: any) => [this.studentId(k), k]));
        const merged = this.students.map(existing => {
          const fresh = byId.get(this.studentId(existing));
          // Preserve the existing session explicitly — this endpoint's
          // response shape (see the reference this was built from) doesn't
          // carry one, but this guards against it clobbering the active
          // auth session if that ever changes.
          return fresh ? { ...existing, ...fresh, session: existing.session } : existing;
        });
        localStorage.setItem('studentDetails', JSON.stringify(merged));
      },
      error: () => { /* photo/kid-details enrichment is best-effort; fail silently */ },
    });
  }

  private fetchInsights(): void {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    const lang = 'en';
    this.insightsEnabled = true;
    this.insightsLoading = true;
    this.parentInsightsService.getInsights(lang).subscribe({
      next: (response: any) => {
        this.insightsLoading = false;
        if (response && response.status && response.body && Array.isArray(response.body.insights)) {
          this.insights = response.body.insights;
        } else {
          this.insightsEnabled = false;
        }
      },
      error: (error) => {
        this.insightsLoading = false;
        if (error?.error?.status_code === 412) {
          // School isn't entitled to this feature — show the section with
          // its "No data found" empty state rather than hiding it outright.
          this.insights = [];
        } else {
          // Any other error hides the section for now since there's no
          // retry/error UI yet.
          this.insightsEnabled = false;
        }
      },
    });
  }

  openInsight(insight: ParentInsight): void {
    this.selectedInsight = insight;
  }

  closeInsight(): void {
    this.selectedInsight = null;
  }

  payNowForFee(): void {
    this.closeInsight();
    this.showSection('fees');
  }

  // Plain-text summary per slug (see the API reference's value-shape
  // table) — kept simple for now rather than the mobile app's mini-charts;
  // getInsightMessage() below covers the fallback insightMessage text every
  // slug also carries.
  insightSummary(insight: ParentInsight): string {
    const v = insight.value || {};
    switch (insight.slug) {
      case 'attendance_percentage_trend':
        return `${v.currentAcademicYearAttendancePercentage ?? '-'}%`;
      case 'absence_day_pattern': {
        const worst = (v.patterns || [])[0];
        return worst ? `${worst.weekday}: ${worst.absentCount}/${worst.totalCount} absent` : '';
      }
      case 'attendance_days_breakdown':
        return v.currentMonth
          ? `${v.currentMonth.monthLabel}: ${v.currentMonth.presentCount} present, ${v.currentMonth.absentCount} absent`
          : '';
      case 'pending_homework':
        return v.today
          ? (v.today.allDoneToday ? 'All done today' : `${v.today.pendingCount} pending today`)
          : '';
      case 'homework_completion_rate': {
        const subjects = v.subjects || [];
        if (!subjects.length) { return ''; }
        const avg = Math.round(subjects.reduce((sum: number, s: any) => sum + (s.completionRate || 0), 0) / subjects.length);
        return `${avg}% average completion`;
      }
      case 'school_presence':
        return v.status ? v.status.replace(/-/g, ' ') : '';
      case 'next_fee_due':
        if (v.state === 'ALL_PAID') { return 'All fees paid'; }
        if (v.state === 'OVERDUE') { return `Overdue: ${v.overdueCount ?? ''}`; }
        return v.amount ? `Due in ${v.daysUntilDue ?? '-'} days` : '';
      case 'absence_homework_impact':
        return `${v.missedCount ?? 0} of ${v.totalDueOnAbsentDays ?? 0} missed on absent days`;
      default:
        return '';
    }
  }

  // ---- Chart data helpers (attendance_percentage_trend, attendance_days_breakdown,
  // homework_completion_rate) — plain SVG, single-series per card so no
  // legend is needed (see dataviz skill: "a single series needs no legend
  // box"). Colors come from the app's own brand variables, never new hex.

  // attendance_percentage_trend: a 2-point mini trend line (previous month
  // -> current year-to-date), value-at-the-end labeled, per marks-and-anatomy's
  // "line -> value at the end" rule. Coordinates are computed directly in
  // the target <svg>'s own viewBox units (width/height passed in) rather
  // than drawn once and CSS-scaled — a non-uniform transform: scale() would
  // distort the 2px stroke into an ellipse-shaped line/dot.
  // topPad is taller than bottomPad on purpose: the end-value label (see
  // the template's <text> above the end-dot) sits above the line, so a
  // near-100% value needs real headroom or its label clips off the top of
  // the viewBox — this was the actual bug (label text getting cut off).
  private trendCoords(insight: ParentInsight, width: number, height: number) {
    const v = insight.value || {};
    const prev = Number(v.previousMonthAttendancePercentage) || 0;
    const curr = Number(v.currentAcademicYearAttendancePercentage) || 0;
    const topPad = height * 0.42;
    const bottomPad = height * 0.12;
    const startX = width * 0.05;
    const endX = width * 0.95;
    const usable = height - topPad - bottomPad;
    const toY = (pct: number) => (height - bottomPad) - (Math.max(0, Math.min(100, pct)) / 100) * usable;
    return { startX, endX, startY: toY(prev), endY: toY(curr) };
  }

  trendLinePoints(insight: ParentInsight, width = 80, height = 40): string {
    const c = this.trendCoords(insight, width, height);
    return `${c.startX.toFixed(1)},${c.startY.toFixed(1)} ${c.endX.toFixed(1)},${c.endY.toFixed(1)}`;
  }

  // Closed polygon under the line down to the baseline, for a soft ~10%-
  // opacity area fill under the trend (see dataviz skill's mark specs) —
  // makes the chart read as a filled trend rather than a bare diagonal.
  trendAreaPoints(insight: ParentInsight, width = 80, height = 40): string {
    const c = this.trendCoords(insight, width, height);
    const baseline = height - height * 0.12;
    return `${c.startX.toFixed(1)},${baseline} ${c.startX.toFixed(1)},${c.startY.toFixed(1)} ${c.endX.toFixed(1)},${c.endY.toFixed(1)} ${c.endX.toFixed(1)},${baseline}`;
  }

  trendEndX(width = 80): number {
    return width * 0.95;
  }

  trendEndY(insight: ParentInsight, height = 40, width = 80): number {
    return this.trendCoords(insight, width, height).endY;
  }

  trendIsUp(insight: ParentInsight): boolean {
    return insight.value?.yearVsLastMonthTrend === 'UP';
  }

  // attendance_days_breakdown: a 4-segment donut (present/absent/late/leave)
  // for the current month, built as stroke-dasharray arcs on one <circle>
  // radius — single hue at 4 lightness steps (still "one series," just
  // segmented) rather than 4 unrelated categorical colors, since these
  // segments are parts of one whole, not independent series.
  donutSegments(insight: ParentInsight): { color: string; dasharray: string; dashoffset: string }[] {
    const month = insight.value?.currentMonth;
    if (!month) { return []; }
    const total = (month.presentCount || 0) + (month.absentCount || 0) + (month.lateCount || 0) + (month.leaveCount || 0);
    if (!total) { return []; }
    const circumference = 2 * Math.PI * 15.5;
    // Present/absent/late/leave are parts of one whole, so this stays a
    // single-hue ramp (brand color at 4 steps) rather than 4 unrelated
    // categorical colors — but translucent black for the smaller segments
    // read as muddy/washed-out on a light card, so use var(--pd-faded-grey)
    // and var(--pd-light-grey) (both already defined on .pd-shell) for
    // clean, flat steps instead of opacity blends.
    const parts = [
      { count: month.presentCount || 0, color: 'var(--brand-primary-color)' },
      { count: month.absentCount || 0, color: 'var(--secondary-primary-color)' },
      { count: month.lateCount || 0, color: 'var(--pd-faded-grey)' },
      { count: month.leaveCount || 0, color: 'var(--pd-light-grey)' },
    ];
    let offset = 0;
    return parts.filter(p => p.count > 0).map(p => {
      const length = (p.count / total) * circumference;
      const segment = {
        color: p.color,
        dasharray: `${length.toFixed(2)} ${(circumference - length).toFixed(2)}`,
        dashoffset: `${(-offset).toFixed(2)}`,
      };
      offset += length;
      return segment;
    });
  }

  donutPresentPercent(insight: ParentInsight): number {
    const month = insight.value?.currentMonth;
    if (!month) { return 0; }
    const total = (month.presentCount || 0) + (month.absentCount || 0) + (month.lateCount || 0) + (month.leaveCount || 0);
    return total ? Math.round((month.presentCount || 0) / total * 100) : 0;
  }

  // homework_completion_rate: horizontal bars, one per subject, capped at
  // 24px thick per marks-and-anatomy — text (subject name, %) stays in ink,
  // never the bar's own color.
  get homeworkSubjectBars(): { subject: string; rate: number }[] {
    if (!this.selectedInsight || this.selectedInsight.slug !== 'homework_completion_rate') {
      return [];
    }
    return (this.selectedInsight.value?.subjects || [])
      .map((s: any) => ({ subject: s.subject, rate: Math.max(0, Math.min(100, s.completionRate || 0)) }));
  }

  insightIcon(slug: string): string {
    const icons: { [key: string]: string } = {
      attendance_percentage_trend: 'ph ph-chart-line-up',
      absence_day_pattern: 'ph ph-calendar-x',
      attendance_days_breakdown: 'ph ph-chart-pie-slice',
      pending_homework: 'ph ph-notebook',
      homework_completion_rate: 'ph ph-list-checks',
      school_presence: 'ph ph-map-pin',
      next_fee_due: 'ph ph-credit-card',
      absence_homework_impact: 'ph ph-warning-circle',
    };
    return icons[slug] || 'ph ph-lightbulb';
  }

  // Status pill + tone for the card (null = no pill). Thresholds mirror the
  // wording of each insight; the numbers themselves come from the API.
  insightStatus(insight: ParentInsight): { key: string; tone: 'good' | 'warn' | 'none' } | null {
    const v = insight.value || {};
    switch (insight.slug) {
      case 'attendance_percentage_trend': {
        const pct = Number(v.currentAcademicYearAttendancePercentage);
        if (isNaN(pct)) { return null; }
        return pct >= 90 ? { key: 'pdOnTrack', tone: 'good' } : { key: 'pdNeedsAttention', tone: 'warn' };
      }
      case 'attendance_days_breakdown':
        if (!v.currentMonth) { return null; }
        if (this.breakdownTotal(insight) === 0) { return { key: 'pdNoDataYet', tone: 'none' }; }
        return Number(v.currentMonth.absentCount) > 0 ? { key: 'pdNeedsAttention', tone: 'warn' } : { key: 'pdOnTrack', tone: 'good' };
      case 'homework_completion_rate': {
        const subjects = v.subjects || [];
        if (!subjects.length) { return null; }
        const avg = subjects.reduce((s: number, x: any) => s + (x.completionRate || 0), 0) / subjects.length;
        return avg < 50 ? { key: 'pdNeedsAttention', tone: 'warn' } : { key: 'pdOnTrack', tone: 'good' };
      }
      case 'pending_homework':
        if (!v.today) { return null; }
        return v.today.allDoneToday ? { key: 'pdAllDone', tone: 'good' } : { key: 'pdNeedsAttention', tone: 'warn' };
      case 'next_fee_due':
        if (v.state === 'ALL_PAID') { return { key: 'pdPaid', tone: 'good' }; }
        if (v.state === 'OVERDUE') { return { key: 'pdOverdue', tone: 'warn' }; }
        return null;
      case 'absence_homework_impact':
        return Number(v.missedCount) > 0 ? { key: 'pdNeedsAttention', tone: 'warn' } : null;
      default:
        return null;
    }
  }

  // Rows of progress bars shown inside a card (homework by subject, absences by
  // weekday, missed homework on absent days). Empty array = card has no bars.
  insightBars(insight: ParentInsight): { label: string; value: string; pct: number; tone: 'good' | 'warn' | 'low' }[] {
    const v = insight.value || {};
    const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n || 0)));
    switch (insight.slug) {
      case 'homework_completion_rate':
        return (v.subjects || []).map((s: any) => {
          const pct = clamp(s.completionRate);
          return { label: s.subject, value: `${pct}%`, pct, tone: (pct >= 60 ? 'good' : pct >= 20 ? 'warn' : 'low') as 'good' | 'warn' | 'low' };
        });
      case 'absence_day_pattern':
        return (v.patterns || []).slice(0, 4).map((p: any) => {
          const total = Number(p.totalCount) || 0;
          const absent = Number(p.absentCount) || 0;
          return { label: p.weekday, value: `${absent}/${total}`, pct: total ? clamp((absent / total) * 100) : 0, tone: (absent > 0 ? 'warn' : 'good') as 'good' | 'warn' | 'low' };
        });
      case 'absence_homework_impact': {
        const total = Number(v.totalDueOnAbsentDays) || 0;
        const missed = Number(v.missedCount) || 0;
        return total ? [{ label: '', value: `${missed}/${total}`, pct: clamp((missed / total) * 100), tone: (missed > 0 ? 'warn' : 'good') as 'good' | 'warn' | 'low' }] : [];
      }
      default:
        return [];
    }
  }

  // Label / value pairs shown as a small facts list under the headline.
  insightFacts(insight: ParentInsight): { label: string; value: string; down?: boolean }[] {
    const v = insight.value || {};
    switch (insight.slug) {
      case 'attendance_days_breakdown': {
        const m = v.currentMonth;
        return m && this.breakdownTotal(insight) > 0 ? [
          { label: 'present', value: String(m.presentCount ?? 0) },
          { label: 'absent', value: String(m.absentCount ?? 0) },
          { label: 'late', value: String(m.lateCount ?? 0) },
          { label: 'leave', value: String(m.leaveCount ?? 0) },
        ] : [];
      }
      case 'attendance_percentage_trend': {
        if (v.previousMonthAttendancePercentage == null) { return []; }
        const diff = Math.round((Number(v.currentAcademicYearAttendancePercentage) || 0) - (Number(v.previousMonthAttendancePercentage) || 0));
        return [
          { label: 'pdPreviousMonth', value: `${v.previousMonthAttendancePercentage}%` },
          { label: 'pdChange', value: `${diff > 0 ? '+' : diff < 0 ? '\u2212' : ''}${Math.abs(diff)} pts`, down: diff < 0 },
        ];
      }
      case 'next_fee_due': {
        const rows: { label: string; value: string }[] = [];
        if (v.amount) { rows.push({ label: 'pdAmountDue', value: String(v.amount) }); }
        if (v.daysUntilDue != null && v.state !== 'ALL_PAID') { rows.push({ label: 'pdDueInDays', value: String(v.daysUntilDue) }); }
        if (v.overdueCount) { rows.push({ label: 'pdOverdue', value: String(v.overdueCount) }); }
        return rows;
      }
      case 'pending_homework':
        return v.today ? [{ label: 'pdPendingToday', value: String(v.today.pendingCount ?? 0) }] : [];
      default:
        return [];
    }
  }

  // ---- Per-card metric (big number + short label), breakdown helpers, scroll ----
  breakdownTotal(insight: ParentInsight): number {
    const m = (insight.value || {}).currentMonth || {};
    return (Number(m.presentCount) || 0) + (Number(m.absentCount) || 0) + (Number(m.lateCount) || 0) + (Number(m.leaveCount) || 0);
  }

  breakdownSegments(insight: ParentInsight): { pct: number; color: string }[] {
    const m = (insight.value || {}).currentMonth || {};
    const total = this.breakdownTotal(insight) || 1;
    const seg = (n: any, color: string) => ({ pct: ((Number(n) || 0) / total) * 100, color });
    return [
      seg(m.presentCount, 'var(--brand-primary-color)'),
      seg(m.lateCount, '#E4932B'),
      seg(m.absentCount, '#C2475A'),
      seg(m.leaveCount, '#8c8c8c'),
    ].filter((s) => s.pct > 0);
  }

  insightMetric(insight: ParentInsight): { value: string; label: string } | null {
    const v = insight.value || {};
    switch (insight.slug) {
      case 'attendance_percentage_trend':
        return { value: `${v.currentAcademicYearAttendancePercentage ?? '-'}%`, label: this.translate.instant('pdThisAcademicYear') };
      case 'attendance_days_breakdown': {
        const m = v.currentMonth;
        if (!m) { return null; }
        const total = this.breakdownTotal(insight);
        return total === 0
          ? { value: `0 ${this.translate.instant('days')}`, label: `${this.translate.instant('pdRecordedIn')} ${m.monthLabel}` }
          : { value: `${m.presentCount ?? 0} / ${total}`, label: `${this.translate.instant('pdDaysPresentIn')} ${m.monthLabel}` };
      }
      case 'homework_completion_rate': {
        const subjects = v.subjects || [];
        if (!subjects.length) { return null; }
        const avg = Math.round(subjects.reduce((s: number, x: any) => s + (x.completionRate || 0), 0) / subjects.length);
        return { value: `${avg}%`, label: `${this.translate.instant('pdAverageAcross')} ${subjects.length} ${this.translate.instant('pdSubjects')}` };
      }
      case 'next_fee_due':
        if (v.state === 'ALL_PAID') {
          return { value: `${this.currentCurrencySymbol}0 ${this.translate.instant('pdDue')}`, label: this.translate.instant('pdAllFeesPaid') };
        }
        return { value: this.insightSummary(insight), label: v.amount ? `${this.currentCurrencySymbol}${v.amount}` : '' };
      default: {
        const s = this.insightSummary(insight);
        return s ? { value: s, label: '' } : null;
      }
    }
  }

  scrollToInsight(slug: string): void {
    const el = document.getElementById('pd-insight-' + slug);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  get attentionCount(): number {
    return (this.insights || []).filter((i) => this.insightStatus(i)?.tone === 'warn').length;
  }

  trackByInsightSlug(index: number, insight: ParentInsight): string {
    return insight.slug;
  }

  private extractErrorMessage(error: any, fallback: string): string {
    return (error && error.error && error.error.body && error.error.body.message)
      || (error && error.error && error.error.message)
      || fallback;
  }

  resendOtp(): void {
    if (this.resendTimeLeft > 0) {
      return;
    }
    this.sendOtp();
  }

  changeMobileNumber(): void {
    this.otpSent = false;
    this.otp = ['', '', '', '', '', ''];
    this.otpErrorMessage = '';
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  private startResendTimer(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
    this.resendTimeLeft = 30;
    this.resendInterval = setInterval(() => {
      this.resendTimeLeft--;
      if (this.resendTimeLeft <= 0) {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  showLogoutConfirm = false;

  requestLogout(): void {
    this.showLogoutConfirm = true;
  }

  cancelLogout(): void {
    this.showLogoutConfirm = false;
  }

  confirmLogout(): void {
    this.showLogoutConfirm = false;
    // Best-effort server-side session invalidation — the local logout below
    // (clearing storage + navigating away) must still happen even if this
    // call fails (expired session, offline, etc.), so the parent is never
    // stuck unable to log out of this device.
    this.parentLoginService.logout().subscribe({
      next: () => {},
      error: () => {},
    });
    localStorage.removeItem('studentDetails');
    localStorage.removeItem('authToken');
    localStorage.removeItem('mobileNumber');
    localStorage.removeItem('userLoginedMode');
    localStorage.removeItem('SELECTED_STUDENT');
    this.loginSuccessful = false;
    this.insights = [];
    this.insightsEnabled = false;
    this.selectedInsight = null;
    this.activeSection = 'dashboard';
    this.router.navigate(['/parent/login']);
  }

  showSection(section: 'dashboard' | 'diary' | 'attendance' | 'timetable' | 'leaveRequest' | 'fees' | 'documentHub' | 'broadcast' | 'chat' | 'notifications' | 'myTeacher' | 'activity' | 'feedback' | 'academicCalendar'): void {
    // Same stale-deep-link/gated-tile guard as the constructor's initial-
    // section check — a nav item that's hidden by *ngIf can't be clicked,
    // but showSection() is also called from other paths (e.g. the
    // notifications panel's own (navigate) output), so it needs the same
    // fallback rather than trusting every caller to only ever pass an
    // enabled section.
    if (!this.isSectionEnabled(section)) {
      section = 'dashboard';
    }
    if (section !== 'chat') { this.chatTargetStaffId = null; }
    this.activeSection = section;
    const routeSegment = section === 'leaveRequest' ? 'leave-request' : section === 'documentHub' ? 'document-hub' : section === 'myTeacher' ? 'my-teacher' : section === 'academicCalendar' ? 'academic-calendar' : section;
    this.router.navigate(['/parent', routeSegment]);
    // No-op on desktop (the drawer flag has no effect there) — on mobile,
    // picking a section should close the drawer instead of leaving it
    // open over the newly-navigated content.
    this.mobileSidebarOpen = false;
  }

  toggleMobileSidebar(): void {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  // Confirmed against the real API (getAllKidInfoForSecondaryParent /
  // schoolAndStudentDetailData): studentId, classId, studentName — see the
  // response shape in that endpoint's reference. Public — used both
  // internally and directly from the template (avatar strip / overflow
  // modal rows).
  studentId(s: any): string {
    return s?.studentId || '';
  }

  private studentClassId(s: any): string {
    return s?.classId || '';
  }

  // NOT yet confirmed against the real API response (unlike studentId/
  // classId/studentName above) — best-guess field names, needed by the
  // attendance calendar's schoolId/academicId query params.
  private studentSchoolId(s: any): string {
    return s?.schoolId || s?.fkSchoolId || '';
  }

  // Confirmed against the real schoolAndStudentDetailData response: the
  // field is currentAcademicId (e.g. 387) — academicId/fkAcademicId/
  // academicYearId never existed on this object, so every API call reading
  // this (fees, attendance, broadcast) was silently sending an empty
  // academicId after login.
  private studentAcademicId(s: any): string {
    return s?.currentAcademicId ?? '';
  }

  studentName(s: any): string {
    return s?.studentName || '';
  }

  // Confirmed against the real schoolAndStudentDetailData response
  // (currencySymbol: "$", currencyType: "USD" on a non-INR/Maples school) —
  // the fees section previously hardcoded the rupee sign everywhere, which
  // is wrong for any school whose fees aren't in INR.
  private studentCurrencySymbol(s: any): string {
    return s?.currencySymbol || '₹';
  }

  // Confirmed against the real API (getAllKidInfoForSecondaryParent /
  // schoolAndStudentDetailData): studentImage, a plain URL carried through
  // as-is with no upload/edit capability — just render it directly.
  studentPhoto(s: any): string {
    return s?.studentImage || '';
  }

  // School name as carried on the login's student record (key spelling varies across responses).
  studentSchoolName(s: any): string {
    return s?.schoolName || s?.SchoolName || s?.school_name || s?.school?.name || '';
  }

  studentClassLabel(s: any): string {
    const num = s?.classNumber || s?.ClassNumber || '';
    const div = s?.classDivision || s?.ClassDivision || '';
    return num ? `${num}${div ? ' - ' + div : ''}` : '';
  }

  // Avatar-strip hover tooltip content — name and class together, per the
  // header's avatar strip tooltip requirement.
  studentTooltip(s: any): string {
    const name = this.studentName(s);
    const classLabel = this.studentClassLabel(s);
    return classLabel ? `${name} · ${classLabel}` : name;
  }

  private studentSession(s: any): string {
    return s?.session || s?.Session || '';
  }

  // Full sibling list, active student always at index 0 — mirrors the
  // mobile app's "kids" array (splice-to-front model, see switchStudent()).
  get students(): any[] {
    const raw = localStorage.getItem('studentDetails');
    if (!raw) { return []; }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private get storedStudent(): any {
    return this.students[0] || null;
  }

  get currentStudentId(): string {
    return this.studentId(this.storedStudent);
  }

  get currentClassId(): string {
    return this.studentClassId(this.storedStudent);
  }

  get currentSchoolId(): string {
    return this.studentSchoolId(this.storedStudent);
  }

  get currentAcademicId(): string {
    return this.studentAcademicId(this.storedStudent);
  }

  get currentStudentName(): string {
    return this.studentName(this.storedStudent);
  }

  get currentCurrencySymbol(): string {
    return this.studentCurrencySymbol(this.storedStudent);
  }

  get currentStudentPhoto(): string {
    return this.studentPhoto(this.storedStudent);
  }

  // ---- Sidebar feature-flag gating — mirrors the mobile app's ClassData.js
  // updateFeatureFlags()/getFeesListData(), which reads these same fields off
  // the logged-in student record (there is no separate feature-flags API;
  // it's the same getAllKidInfoForSecondaryParent payload already stored in
  // studentDetails). Deliberately NOT unified into one shape even though
  // featureList/schoolExtensionServicePermissions/feesSettings are three
  // differently-shaped structures for what is conceptually "is this tile
  // on" — that's how the mobile app itself keeps them, and this is meant to
  // replicate it as closely as possible rather than clean it up.

  // featureList -> { [BluewingsFeatureName]: entry } lookup, same shape as
  // ClassData.js's featureMap. Recomputed from the current student on every
  // access (cheap, small array) rather than cached, so it always reflects
  // whichever student switchStudent() just made active.
  private get currentFeatureMap(): { [key: string]: any } {
    const list: any[] = this.storedStudent?.featureList || [];
    const map: { [key: string]: any } = {};
    for (const entry of list) {
      if (entry && entry.BluewingsFeatureName) {
        map[entry.BluewingsFeatureName] = entry;
      }
    }
    return map;
  }

  // A tile with no matching featureList entry is treated as disabled
  // (optional-chained lookup naturally falls through to false) — but if
  // featureList itself is missing/empty on the student record (an older or
  // differently-shaped API response), fail OPEN instead: show every
  // featureList-gated tile rather than silently blanking the whole sidebar
  // for a shape mismatch that has nothing to do with actual per-school
  // entitlements.
  //
  // isFutureAcademic is ANDed onto every featureList-gated tile, exactly as
  // ClassData.js's getFeesListData() does ("!isFutureAcademic" on every
  // mapped item) — viewing a past/future academic year (via the academic
  // year switcher) force-disables these regardless of the school's actual
  // entitlements for the CURRENT year.
  private isFeatureEnabled(featureKey: string): boolean {
    const student = this.storedStudent;
    if (student?.isFutureAcademic) {
      return false;
    }
    if (!student || !Array.isArray(student.featureList) || !student.featureList.length) {
      return true;
    }
    return this.currentFeatureMap[featureKey]?.ActiveStatus === 'ENABLED';
  }

  // schoolExtensionServicePermissions -> { [serviceName]: entry } lookup —
  // a second, differently-shaped gating structure from the same student
  // record (see the comment above currentFeatureMap). Used by tiles whose
  // mobile-app gate reads this array instead of featureList (Academic
  // Calendar, Check-In/Check-Out) — kept separate rather than merged into
  // currentFeatureMap since the two arrays use different key/value shapes
  // (serviceName/serviceStatus vs BluewingsFeatureName/ActiveStatus).
  private get currentServicePermissionMap(): { [key: string]: any } {
    const list: any[] = this.storedStudent?.schoolExtensionServicePermissions || [];
    const map: { [key: string]: any } = {};
    for (const entry of list) {
      if (entry && entry.serviceName) {
        map[entry.serviceName] = entry;
      }
    }
    return map;
  }

  // Same fail-open rationale as isFeatureEnabled() above, same
  // isFutureAcademic override.
  private isServicePermissionEnabled(serviceName: string): boolean {
    const student = this.storedStudent;
    if (student?.isFutureAcademic) {
      return false;
    }
    if (!student || !Array.isArray(student.schoolExtensionServicePermissions) || !student.schoolExtensionServicePermissions.length) {
      return true;
    }
    return this.currentServicePermissionMap[serviceName]?.serviceStatus === 'ENABLED';
  }

  get isDiaryEnabled(): boolean {
    return this.isFeatureEnabled('DIGITAL_DIARY');
  }

  get isAttendanceEnabled(): boolean {
    return this.isFeatureEnabled('ATTENDANCE');
  }

  get isTimetableEnabled(): boolean {
    return this.isFeatureEnabled('TIME_TABLE_MANAGEMENT');
  }

  get isLeaveRequestEnabled(): boolean {
    return this.isFeatureEnabled('STUDENT_LEAVE_MANAGEMENT');
  }

  get isActivityEnabled(): boolean {
    return this.isFeatureEnabled('ACTIVITY_TIMELINE');
  }

  get isChatEnabled(): boolean {
    return this.isFeatureEnabled('STAFF_PARENT_CHAT');
  }

  get isBroadcastEnabled(): boolean {
    return this.isFeatureEnabled('BROADCAST_VIEW');
  }

  // Gated on schoolExtensionServicePermissions, not featureList — previously
  // had no gate at all (always shown), unlike the mobile app's dashboard
  // where this tile is omitted entirely when the school hasn't purchased
  // this extension service.
  get isAcademicCalendarEnabled(): boolean {
    return this.isServicePermissionEnabled('ACADEMIC_CALENDAR');
  }

  // Fees is gated on a completely different structure
  // (feesSettings[].isFeesModuleEnable, matched by SettingName), not
  // featureList — same getFeesListData() split the mobile app itself has.
  // feesSettings is an ARRAY of setting rows (confirmed against a real
  // student profile response), not a map keyed by SettingName — look the
  // row up by SettingName instead of property access. Missing feesSettings
  // (or no matching row) fails open too, for the same reason as
  // isFeatureEnabled() above — an older/differently-shaped student record
  // shouldn't hide Fees.
  private feesSetting(settingName: string): any {
    const feesSettings = this.storedStudent?.feesSettings;
    if (!Array.isArray(feesSettings)) { return null; }
    return feesSettings.find((s: any) => s?.SettingName === settingName) || null;
  }

  get isFeesEnabled(): boolean {
    const setting = this.feesSetting('MOBILE_FEES_MODULE');
    if (!setting) { return true; }
    return setting.isFeesModuleEnable === 'ENABLED';
  }

  // "Must pay the oldest fee first" school setting — confirmed against a
  // real student profile response: schoolSettings.isFeeSequenceEnabled is
  // a 1/0 flag (not ENABLED/DISABLED like the featureList-based gates
  // above). Missing schoolSettings fails open to false (free selection),
  // same rationale as the other gates on this component — an
  // older/differently-shaped student record shouldn't force sequencing.
  get isFeeSequenceEnabled(): boolean {
    const schoolSettings = this.storedStudent?.schoolSettings;
    if (!schoolSettings) { return false; }
    return schoolSettings.isFeeSequenceEnabled === 1;
  }

  // My Teacher, Document Hub and Feedback have no gate in the mobile spec at
  // all (always enabled) — kept as tiny getters anyway (rather than leaving
  // the template ungated) purely so a future real gate is a one-line change
  // here instead of a template edit. Notifications is NOT one of the mobile
  // app's tiles at all (a web-only addition from an earlier session), so
  // there's no corresponding mobile feature-flag to check it against either
  // — left always-enabled in the template with no getter, matching its
  // existing ungated behavior.

  // Sections that ARE feature-mapped tiles, keyed the same way as
  // activeSection, for the "stale deep link to a now-gated-off section"
  // fallback in the constructor/showSection init chain below.
  private isSectionEnabled(section: string): boolean {
    switch (section) {
      case 'diary': return this.isDiaryEnabled;
      case 'attendance': return this.isAttendanceEnabled;
      case 'timetable': return this.isTimetableEnabled;
      case 'leaveRequest': return this.isLeaveRequestEnabled;
      case 'activity': return this.isActivityEnabled;
      case 'chat': return this.isChatEnabled;
      case 'broadcast': return this.isBroadcastEnabled;
      case 'fees': return this.isFeesEnabled;
      case 'academicCalendar': return this.isAcademicCalendarEnabled;
      default: return true;
    }
  }

  // First 3 siblings for the avatar strip (active + up to 2 more) — the
  // rest are reachable only through the "+N" overflow modal, per the
  // mobile app's UI (never a swipe carousel).
  get avatarStripStudents(): any[] {
    return this.students.slice(0, 3);
  }

  get overflowStudentCount(): number {
    return Math.max(0, this.students.length - 3);
  }

  studentInitial(s: any): string {
    const name = this.studentName(s);
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  isActiveStudent(s: any): boolean {
    return this.studentId(s) === this.currentStudentId;
  }

  openAllStudentsModal(): void {
    this.showAllStudentsModal = true;
  }

  closeAllStudentsModal(): void {
    this.showAllStudentsModal = false;
  }

  // Tapping the active avatar (index 0) previews its photo — it's already
  // the selected student, so unlike a sibling tap this never switches.
  openPhotoPreview(): void {
    this.showPhotoPreview = true;
  }

  closePhotoPreview(): void {
    this.showPhotoPreview = false;
  }

  // Mirrors handleSelectedStudnet() in the mobile app: find by studentId
  // (never by array index — indices aren't trusted), splice that student
  // to the front, swap the active session/authToken to that student's own
  // session, and persist both the "restore on reload" studentId key and
  // the full active-student record. A manual switch is meant to be
  // instant/local — it does not refetch the sibling list from the API,
  // only re-fetches insights for the newly active student.
  switchStudent(clickedStudentId: string): void {
    this.switchStudentError = '';
    if (!clickedStudentId || clickedStudentId === this.currentStudentId) {
      this.closeAllStudentsModal();
      return;
    }
    const current = this.students;
    const index = current.findIndex(s => this.studentId(s) === clickedStudentId);
    if (index === -1) {
      return;
    }
    const [selected] = current.splice(index, 1);
    const session = this.studentSession(selected);
    if (!session) {
      // Mirrors updateHeaderSession()'s abort-on-no-session behavior — a
      // student record with no session token can't become the active one.
      // eslint-disable-next-line no-console
      console.warn('[switchStudent] no session on record for', clickedStudentId, selected);
      this.switchStudentError = 'Unable to switch student — no active session found.';
      return;
    }
    const reordered = [selected, ...current];
    localStorage.setItem('studentDetails', JSON.stringify(reordered));
    localStorage.setItem('authToken', JSON.stringify(session));
    localStorage.setItem('SELECTED_STUDENT', clickedStudentId);

    this.closeAllStudentsModal();
    this.closePhotoPreview();
    // Every feature screen scopes its data to whichever student is active —
    // a switch must refresh all of them, not just insights, or a screen the
    // parent already had open keeps showing the PREVIOUS student's data
    // (e.g. diary/attendance/timetable would only refresh on their next
    // ngOnChanges, and leave-request has no @Input() to react to at all).
    this.insights = [];
    this.selectedInsight = null;
    this.fetchInsights();
    this.studentSwitchTick++;
    // A sibling can belong to a different school with different feature
    // flags — re-check the currently-open section against the NEWLY active
    // student rather than leave activeSection pointed at a tile that's
    // suddenly gated off (and now hidden by *ngIf) for this student.
    if (!this.isSectionEnabled(this.activeSection)) {
      this.showSection('dashboard');
    }
  }
}
